import { cn } from '@/lib/tailwind.utils'
import { RiskProfileStatsCard, type RiskProfileStatsCardProps } from './RiskProfileStatsCard'
import type {
  RiskProfileMetric,
  RiskProfileMetricOption,
  RiskProfileResponse,
} from '../types/riskProfile.types'
import { formatRiskProfileValue } from '../utils/riskProfile.utils'
import { useAtomValue } from 'jotai'
import { currencySymbolAtom } from '@app/state/clientsAtom'

export type RiskProfileStatsGridProps = {
  hazardData?: RiskProfileResponse
  elementData?: RiskProfileResponse
  metricOption: RiskProfileMetricOption
  isLoading?: boolean
  className?: string
}

const labels: Record<RiskProfileMetric, string> = {
  damage_cost: 'Damage cost',
  damage_severity: 'Damage severity',
  quantity: 'Quantity',
  replacement_cost: 'Replacement Cost',
}
export const RiskProfileStatsGrid = ({
  className,
  hazardData,
  elementData,
  metricOption,
  isLoading,
}: RiskProfileStatsGridProps) => {
  const label = labels[metricOption.metric]
  const currencySymbol = useAtomValue(currencySymbolAtom)
  const defaultUnit =
    metricOption.metric === 'damage_cost' || metricOption.metric === 'replacement_cost'
      ? currencySymbol
      : null

  let stats: RiskProfileStatsCardProps[] = []

  // Make a copy of the metric option forcing to 'total' instead
  // of percent values (not available in grand totals yet)
  const statsMetricOption: RiskProfileMetricOption = {
    metric: metricOption.metric,
    type: 'total',
    value: metricOption.value,
    label: metricOption.label,
  }

  if (elementData) {
    const topElementGroup = elementData.group_totals.toSorted(
      (groupA, groupB) => groupB.value - groupA.value,
    )[0]
    const totalValue = elementData.grand_total[0]?.value

    if (metricOption.value != 'quantity') {
      stats.push({
        label: `Profile ${label}`,
        value: formatRiskProfileValue(totalValue, statsMetricOption, defaultUnit),
        description: 'Of replacement value',
        tooltipDescription: `Estimated ${label.toLocaleLowerCase()} across the selected areas, hazards and assets.`,
      })
    }
    stats.push({
      label: 'Most damaged element',
      value: `${topElementGroup.group_key} · ${formatRiskProfileValue(topElementGroup.value, statsMetricOption, defaultUnit)}`,
      description: `By ${label.toLocaleLowerCase()}`,
      tooltipDescription: `Elements contributing the highest ${label.toLocaleLowerCase()}.`,
    })
  }

  if (hazardData && metricOption.value != 'quantity') {
    const topHazardGroup = hazardData.group_totals.toSorted(
      (groupA, groupB) => groupB.value - groupA.value,
    )[0]
    stats.push({
      label: 'Highest impact hazard',
      value: topHazardGroup.group_key,
      description: `${formatRiskProfileValue(topHazardGroup.value, statsMetricOption, defaultUnit)}`,
      tooltipDescription: `Hazard group with the highest overall ${label.toLocaleLowerCase()}`,
    })
  }

  return (
    <div className="w-full">
      <div className="w-full my-8 max-w-full mx-auto">
        <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3', className)}>
          {stats.map((stat) => (
            <RiskProfileStatsCard key={stat.label} {...stat} isLoading={isLoading} />
          ))}
        </div>
      </div>
    </div>
  )
}
